import { Box, Typography } from "@mui/material";
import { memo } from "react";
import { FONT_SIZE, INK } from "../../../../theme/tokens";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";

type Props = {
	/** The single line the cell shows; ellipsized when the column is too narrow for it. */
	text: string;
	/** Extra detail revealed on hover (e.g. the email behind a first name). Omit or pass `null` when the
	 * hover would only repeat the cell — then no tooltip is mounted at all. */
	tooltip?: string | null;
	/** Lands on the text element. */
	dataTestId?: string;
};

/**
 * One line of secondary grid text, vertically centred in a `SystemDataGrid` cell (MUI's plain-text
 * centring stops applying once a Typography sets its own line-height), with an optional hover detail.
 *
 * The tooltip DESCRIBES the text rather than naming it (`describeChild`), so a screen reader announces
 * the visible value first and the detail second — the default would replace the visible text with the
 * tooltip as the element's accessible name.
 *
 * Generic on purpose: any cell whose visible value has a longer or more exact form behind it (a first
 * name and its email, a short code and its full name) renders the same way.
 */
export const TooltipTextCell = memo(function TooltipTextCell({ text, tooltip, dataTestId }: Props) {
	const content = (
		<Typography
			data-testid={dataTestId}
			sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
		>
			{text}
		</Typography>
	);
	return (
		<Box sx={{ display: "flex", alignItems: "center", height: "100%", minWidth: 0 }}>
			{tooltip ? (
				<InHouseTooltip title={tooltip} describeChild>
					{content}
				</InHouseTooltip>
			) : (
				content
			)}
		</Box>
	);
});
